import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SharedStream } from '@/lib/shared-types';
import { toast } from 'sonner';
import { dashboardApi } from '@/lib/services/dashboard-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Video, ArrowRight, Clock, Eye, Play, Users } from 'lucide-react';
import { formatDistanceToNowSafe } from '@/lib/utils/date';
import { MediaImage } from '@/components/ui/media-image';

interface UserStreamsListProps {
  onViewMore?: () => void;
  maxStreams?: number;
}

export function UserStreamsList({ onViewMore, maxStreams = 5 }: UserStreamsListProps) {
  const navigate = useNavigate();
  const [streams, setStreams] = useState<SharedStream[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchUserStreams();
  }, []);

  const fetchUserStreams = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const data = await dashboardApi.getUserStreams(maxStreams);
      setStreams(data);
    } catch (err) {
      console.error('Error fetching user streams:', err);
      setError('Failed to load your streams');
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinStream = async (streamId: number) => {
    try {
      const url = await dashboardApi.joinStream(streamId);
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Error joining stream:', error);
      toast.error('Failed to join stream');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'live':
        return 'bg-red-100 text-red-800 hover:bg-red-200';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800 hover:bg-blue-200';
      case 'ended':
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
    }
  };

  const formatScheduledTime = (scheduledStart?: string) => {
    if (!scheduledStart) return 'Unknown time';
    const date = new Date(scheduledStart);
    if (Number.isNaN(date.getTime())) return 'Unknown time';
    const now = new Date();

    if (date > now) {
      return formatDistanceToNowSafe(date);
    } else {
      return `Started ${formatDistanceToNowSafe(date, 'Unknown time')}`;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" />
            My Streams
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(maxStreams)].map((_, i) => (
              <div key={i} className="flex items-center space-x-3 animate-pulse">
                <div className="w-12 h-12 bg-gray-200 rounded"></div>
                <div className="flex-1 space-y-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" />
            My Streams
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchUserStreams}
            >
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Video className="h-5 w-5" />
          My Streams
        </CardTitle>
      </CardHeader>
      <CardContent>
        {streams.length === 0 ? (
          <div className="text-center py-6">
            <Video className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No streams available</p>
          </div>
        ) : (
          <div className="space-y-3">
            {streams.map((stream) => (
              <div key={stream.id} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                <button
                  onClick={() => navigate(`/streams/${stream.id}`)}
                  className="flex items-center space-x-3 flex-1 text-left hover:bg-transparent"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                    {stream.thumbnailUrl ? (
                      <MediaImage
                        src={stream.thumbnailUrl}
                        alt={stream.title}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <Video className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium truncate">{stream.title}</p>
                      <Badge
                        variant="secondary"
                        className={`text-xs px-1.5 py-0.5 ${getStatusColor(stream.status)}`}
                      >
                        {stream.status}
                      </Badge>
                    </div>

                    {stream.description && (
                      <p className="text-sm text-muted-foreground mb-1 line-clamp-2">
                        {stream.description}
                      </p>
                    )}

                    <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2">
                      {stream.creator && (
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          <span>{stream.creator.name}</span>
                        </div>
                      )}

                      {stream.viewerCount !== undefined && (
                        <div className="flex items-center gap-1">
                          <Eye className="h-3 w-3" />
                          <span>{stream.viewerCount} watching</span>
                        </div>
                      )}

                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {stream.scheduledStart
                          ? formatScheduledTime(stream.scheduledStart)
                          : formatDistanceToNowSafe(stream.createdAt)
                        }
                      </div>
                    </div>
                  </div>
                </button>

                <div className="flex items-center gap-2">
                  {stream.status === 'live' && (
                    <Button
                      size="sm"
                      onClick={() => handleJoinStream(stream.id)}
                      className="h-7 px-3 bg-red-600 hover:bg-red-700"
                    >
                      <Play className="h-3 w-3 mr-1" />
                      Join Live
                    </Button>
                  )}

                  {stream.status === 'scheduled' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleJoinStream(stream.id)}
                      className="h-7 px-3"
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      View Details
                    </Button>
                  )}

                  {stream.status === 'ended' && stream.playbackUrl && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleJoinStream(stream.id)}
                      className="h-7 px-3"
                    >
                      <Play className="h-3 w-3 mr-1" />
                      Watch Recording
                    </Button>
                  )}
                </div>
              </div>
            ))}

            {onViewMore && (
              <div className="pt-2 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onViewMore}
                  className="w-full justify-between"
                >
                  Browse All Streams
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
